import { useState, useEffect } from "react";
import { LogOut, Bell, BellOff, User, TestTube, RotateCcw } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import {
  subscribeToPushNotifications,
  unsubscribeFromPushNotifications,
  isSubscribedToPushNotifications,
  isPushNotificationSupported,
  getNotificationPermission,
  sendTestNotification,
} from "../lib/notifications";
import { Toast } from "./Toast";

interface ToastState {
  message: string;
}

interface SettingsProps {
  onRestartOnboarding?: () => void;
}

export function Settings({ onRestartOnboarding }: SettingsProps) {
  const { user, signOut } = useAuth();
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [supported, setSupported] = useState(false);
  const [permission, setPermission] = useState<NotificationPermission | null>(
    null
  );
  const [toast, setToast] = useState<ToastState | null>(null);

  useEffect(() => {
    checkStatus();
  }, []);

  const checkStatus = async () => {
    setLoading(true);
    const isSupported = await isPushNotificationSupported();
    setSupported(isSupported);

    if (isSupported) {
      const subscribed = await isSubscribedToPushNotifications();
      setIsSubscribed(subscribed);
      setPermission(getNotificationPermission());
    }
    setLoading(false);
  };

  const handleToggleNotifications = async () => {
    setLoading(true);
    try {
      if (isSubscribed) {
        const success = await unsubscribeFromPushNotifications();
        if (success) {
          setIsSubscribed(false);
          setToast({ message: "Notifications disabled" });
        } else {
          setToast({ message: "Failed to disable notifications" });
        }
      } else {
        const success = await subscribeToPushNotifications();
        if (success) {
          setIsSubscribed(true);
          setPermission("granted");
          setToast({
            message:
              "Notifications enabled! You will be notified when it's time to take your supplements.",
          });
        } else {
          setToast({
            message:
              "Failed to enable notifications. Please check your browser settings.",
          });
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const handleTestNotification = async () => {
    setLoading(true);
    try {
      const success = await sendTestNotification();
      if (success) {
        setToast({
          message: "Test notification sent! Check your notifications.",
        });
      } else {
        setToast({ message: "Failed to send test notification" });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSignOut = async () => {
    await signOut();
  };

  return (
    <div className="max-w-4xl mx-auto p-4 safe-area-x">
      <h1 className="text-2xl font-bold text-warm-dark mb-6">Settings</h1>

      <div className="bg-warm-card rounded-xl border border-warm divide-y divide-warm warm-shadow">
        <div className="p-4 flex items-center gap-3">
          <div className="bg-warm-light rounded-full p-3">
            <User className="text-warm-secondary" size={24} />
          </div>
          <div className="flex-1">
            <p className="text-sm text-gray-700">Signed in as</p>
            <p className="font-medium text-warm-dark">{user?.email}</p>
          </div>
        </div>

        <div className="p-4">
          <div className="flex items-start gap-3">
            <div
              className={`rounded-full p-3 ${
                isSubscribed ? "bg-warm-light" : "bg-gray-100"
              }`}
            >
              {isSubscribed ? (
                <Bell className="text-warm-secondary" size={24} />
              ) : (
                <BellOff className="text-gray-600" size={24} />
              )}
            </div>
            <div className="flex-1">
              <p className="font-medium text-warm-dark mb-1">
                Push Notifications
              </p>
              <p className="text-sm text-gray-700 mb-3">
                Get reminded when it's time to take supplements
              </p>

              {!supported && (
                <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 px-3 py-2 rounded-lg text-sm mb-3">
                  Push notifications are not supported in this browser
                </div>
              )}

              {supported && permission === "denied" && (
                <div className="bg-red-50 border border-red-200 text-red-800 px-3 py-2 rounded-lg text-sm mb-3">
                  Notification permission denied. Please enable in browser
                  settings.
                </div>
              )}

              {supported && (
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={handleToggleNotifications}
                    disabled={loading}
                    className={`px-4 py-2 rounded-lg font-medium transition-colors disabled:opacity-50 ${
                      isSubscribed
                        ? "bg-red-100 text-red-700 hover:bg-red-200"
                        : "bg-warm-primary text-white hover:bg-warm-hover warm-shadow"
                    }`}
                  >
                    {loading
                      ? "Loading..."
                      : isSubscribed
                      ? "Disable"
                      : "Enable"}
                  </button>

                  {isSubscribed && (
                    <button
                      onClick={handleTestNotification}
                      disabled={loading}
                      className="px-4 py-2 bg-warm-glass text-warm-dark rounded-lg font-medium hover:bg-warm-light disabled:opacity-50 flex items-center gap-2 border border-warm"
                    >
                      <TestTube size={16} />
                      Test
                    </button>
                  )}
                </div>
              )}

              {isSubscribed && (
                <div className="mt-3 p-3 bg-warm-light rounded-lg">
                  <p className="text-sm text-warm-secondary">
                    Notifications will be sent for each scheduled supplement in
                    your daily plan.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {onRestartOnboarding && (
          <button
            onClick={onRestartOnboarding}
            className="w-full p-4 flex items-center gap-3 hover:bg-warm-light transition-colors text-left border-b border-warm"
          >
            <RotateCcw className="text-warm-secondary" size={24} />
            <div className="flex-1">
              <p className="font-medium text-warm-secondary">
                Restart Tutorial
              </p>
              <p className="text-sm text-gray-700">
                Show the onboarding guide again
              </p>
            </div>
          </button>
        )}

        <button
          onClick={handleSignOut}
          className="w-full p-4 flex items-center gap-3 hover:bg-red-50 transition-colors text-left"
        >
          <LogOut className="text-red-600" size={24} />
          <div className="flex-1">
            <p className="font-medium text-red-600">Sign Out</p>
            <p className="text-sm text-gray-600">Log out of your account</p>
          </div>
        </button>
      </div>

      <div className="mt-8 p-4 bg-gray-50 rounded-xl border border-gray-200">
        <h2 className="font-semibold text-gray-900 mb-2">About</h2>
        <p className="text-sm text-gray-600 mb-2">
          Pill Scheduler helps you manage your supplement routine with smart
          scheduling based on your wake time and custom gap rules between
          supplements.
        </p>
        <p className="text-xs text-gray-500">Version 1.0.0</p>
      </div>

      {toast && (
        <Toast message={toast.message} onClose={() => setToast(null)} />
      )}
    </div>
  );
}
